/**
 * The app's navigation model, in one place — the primary rail and the
 * section nav both read from it, so a new page is one entry, not two.
 *
 * Two levels: you pick a section in the rail, which takes you into it, and
 * its pages appear in a second column (see SectionNav). Sections with a
 * single page skip that column — a sub-nav listing one link is pure chrome.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: string;
}

export interface NavSection {
  key: string;
  label: string;
  icon: string;
  items: NavItem[];
}

/** Cross-cutting tools that belong to no section. Chat is the landing page. */
export const ROOT_ITEMS: NavItem[] = [
  { to: "/chat", label: "Chat", icon: "fa-message" },
  { to: "/docs", label: "Documents", icon: "fa-folder-open" },
];

export const SECTIONS: NavSection[] = [
  {
    key: "development",
    label: "Development",
    icon: "fa-code",
    items: [
      { to: "/board", label: "Board", icon: "fa-columns" },
      { to: "/deployments", label: "Deployments", icon: "fa-rocket" },
      { to: "/env", label: "Env vars", icon: "fa-table-cells" },
    ],
  },
  {
    // "Business", not "Customers": only one of these four pages is customers.
    key: "business",
    label: "Business",
    icon: "fa-briefcase",
    items: [
      { to: "/customers", label: "Customers", icon: "fa-building-user" },
      { to: "/pipeline", label: "Pipeline", icon: "fa-filter-circle-dollar" },
      { to: "/investors", label: "Investors", icon: "fa-sack-dollar" },
      { to: "/competition", label: "Competition", icon: "fa-chess-knight" },
    ],
  },
  {
    // Bank feed (/finance/feed) is deliberately absent while TrueLayer
    // onboarding is pending — the route still works, it just isn't advertised.
    key: "finance",
    label: "Finance",
    icon: "fa-coins",
    items: [
      { to: "/finance", label: "Ledger", icon: "fa-list" },
      { to: "/finance/statements", label: "Statements", icon: "fa-chart-pie" },
      { to: "/finance/insights", label: "Insights", icon: "fa-chart-line" },
      { to: "/finance/accounts", label: "Accounts", icon: "fa-sitemap" },
    ],
  },
  {
    key: "compliance",
    label: "Compliance",
    icon: "fa-shield-halved",
    items: [
      { to: "/compliance", label: "Overview", icon: "fa-gauge-high" },
      { to: "/compliance/controls", label: "Controls", icon: "fa-list-check" },
      { to: "/compliance/policies", label: "Policies", icon: "fa-file-shield" },
      { to: "/compliance/operating", label: "Operating", icon: "fa-calendar-check" },
      { to: "/compliance/risks", label: "Risks", icon: "fa-triangle-exclamation" },
      { to: "/compliance/soa", label: "SoA", icon: "fa-clipboard-check" },
      { to: "/compliance/people", label: "People", icon: "fa-users" },
      { to: "/compliance/vendors", label: "Vendors", icon: "fa-handshake" },
      { to: "/compliance/audit", label: "Internal audit", icon: "fa-magnifying-glass-chart" },
      { to: "/compliance/connectors", label: "Collectors", icon: "fa-plug" },
      { to: "/compliance/frameworks", label: "Frameworks", icon: "fa-scale-balanced" },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    icon: "fa-sliders",
    items: [
      { to: "/team", label: "Team", icon: "fa-users" },
      { to: "/audit", label: "Audit log", icon: "fa-clock-rotate-left" },
    ],
  },
];

/** True when `pathname` is the item's page or a drill-down beneath it. */
export const matchesItem = (item: NavItem, pathname: string): boolean =>
  pathname === item.to || pathname.startsWith(item.to + "/");

/**
 * Longest match wins, so /compliance/controls picks Controls rather than the
 * Overview it also prefixes. That is why items need no `end` flag.
 */
export function activeItem(section: NavSection, pathname: string): NavItem | undefined {
  return section.items
    .filter((i) => matchesItem(i, pathname))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

export function sectionForPath(pathname: string): NavSection | undefined {
  return SECTIONS.find((s) => s.items.some((i) => matchesItem(i, pathname)));
}

/** Where the rail sends you when you enter a section. */
export const sectionHome = (section: NavSection): string => section.items[0].to;
